/**
 * A shareholder's own holding, and the cap table — decision 13, overruled.
 *
 * 13 said only admins may see that somebody is a shareholder. Bjarte overruled
 * it on 29 Aug 2026 on the grounds that an aksjeeierbok is a document a
 * shareholder is entitled to read. The overrule is about *who owns what* and
 * nothing else, which is why this screen reads two different things:
 *
 *  - the caller's **own** shareholder record, by an explicit link on their
 *    allowlist row — their shares, their prices, their own details;
 *  - **`skidaCrmCapTable/current`**, a generated projection holding three fields
 *    per holder. Not the register. Everything the overrule did not cover —
 *    personnummer, addresses, contact details, who has paid — is not in the
 *    document this screen can read, which is a stronger guarantee than a rule
 *    that has to keep being right about field names.
 *
 * ## Two more reads, for the receipt — 2 Sep 2026
 *
 * A kvittering for innbetalt aksjekapital has to state facts that are not on
 * the holder's own record, so the screen also reads `skidaCrmSettings/company`
 * for the company that received the money and the pålydende, and one
 * `skidaCrmIssuances` document per round the holder actually has shares in, for
 * the tegningskurs — which most purchases do not carry, because absent means
 * the round's price.
 *
 * Both are narrow on purpose and neither is a projection: a receipt has to be
 * available the day a payment is registered, and a generated document would be
 * as stale as the script that last wrote it. The narrowing is `get` without
 * `list` on the rounds, so the only ones reachable are the ids already on the
 * caller's own purchases. See the rules for both.
 */
import { useEffect, useMemo, useState } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { useAuth } from '@/features/auth/AuthProvider'
import { nf } from '@/shared/util/numbers'
import { fmtKrPrice, fmtKrSum } from '@/shared/util/money'
import { formatDate, todayIso } from '@/shared/util/dates'
import { EMPTY_COMPANY, type CompanyDetails } from '@/types/company'
import type { Issuance, Shareholder } from '@/types/ownership'
import {
  buildCertificate,
  registeredShares,
  buildReceipt,
  certifiableTransactions,
  paidTransactions,
} from '@/types/shareDocuments'
import { ShareReceipt } from '@/features/ownership/ShareReceipt'
import { ShareCertificate } from '@/features/ownership/ShareCertificate'
import { CapTablePie } from './CapTablePie'
import { ShareholderArchiveCard } from './ShareholderArchiveCard'
import {
  subscribeToShareholderDocs,
  type ShareholderDoc,
} from '@/features/ownership/repository/shareholderDocsRepository'

interface CapRow {
  name: string
  shares: number
  pct: number | null
}

/**
 * One movement of shares this shareholder was a party to.
 *
 * Generated by `scripts/buildCapTable.ts` with the counterparty already named,
 * because resolving `fromId`/`toId` means reading records this screen cannot
 * read. See the rule on `skidaCrmHolderTransfers`.
 */
interface TransferRow {
  date: string
  shares: number
  direction: 'in' | 'out'
  counterparty: string
  pricePerShare: number | null
  settled: boolean
  buyback: boolean
}

/** One line of "how you got them": shares issued, or shares that changed hands. */
type Acquisition =
  | {
      kind: 'purchase'
      key: string
      date: string
      shares: number
      pricePerShare: number | null
      paidAt: string
    }
  /**
   * A grant taken up and paid for — added 2 Sep 2026 with the receipts.
   *
   * These shares were always counted (`exercisedShares` requires `paidDate`)
   * and were the one way of coming to own part of the company this screen
   * never showed, so a holder whose whole position came from options read
   * "no shares are registered against you" beside a percentage. Offering a
   * receipt for a payment the screen does not admit happened would have been
   * the same bug wearing a button.
   */
  | {
      kind: 'exercise'
      key: string
      date: string
      shares: number
      pricePerShare: number | null
      paidAt: string
    }
  | {
      kind: 'transfer'
      key: string
      date: string
      shares: number
      pricePerShare: number | null
      direction: 'in' | 'out'
      counterparty: string
      settled: boolean
    }

export function ShareholderScreen() {
  const { user } = useAuth()
  const linkedId = user?.shareholderId ?? ''
  const [me, setMe] = useState<Shareholder | null>(null)
  const [cap, setCap] = useState<{ rows: CapRow[]; total: number; builtAt: string } | null>(null)
  /**
   * The shared archive. Its own subscription, and a failure is a quiet one:
   * a shareholder who cannot see the documents should still see their holding,
   * and an error banner over a cap table about a file list would be the tail
   * wagging the dog.
   */
  const [docs, setDocs] = useState<ShareholderDoc[]>([])
  const [moves, setMoves] = useState<TransferRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  /** What a receipt needs beyond the holder's own record. */
  const [company, setCompany] = useState<CompanyDetails>(EMPTY_COMPANY)
  const [issuances, setIssuances] = useState<Issuance[]>([])
  /**
   * Which document is open, if any — one piece of state for both, so a receipt
   * and a certificate can never be on screen at once fighting over the printer.
   */
  const [openDoc, setOpenDoc] = useState<{ kind: 'receipt' | 'certificate'; key: string } | null>(
    null,
  )

  useEffect(() => subscribeToShareholderDocs(setDocs, () => setDocs([])), [])

  useEffect(() => {
    let live = true
    setLoading(true)
    void (async () => {
      try {
        const [mine, table, transfers] = await Promise.all([
          linkedId ? getDoc(doc(db, 'skidaCrmShareholders', linkedId)) : Promise.resolve(null),
          getDoc(doc(db, 'skidaCrmCapTable', 'current')),
          // Keyed by the caller's own id, which is the only one the rule lets
          // through -- so there is nothing here to filter on the client.
          linkedId
            ? getDoc(doc(db, 'skidaCrmHolderTransfers', linkedId))
            : Promise.resolve(null),
        ])
        if (!live) return
        setMe(mine?.exists() ? ({ id: mine.id, ...mine.data() } as Shareholder) : null)
        setMoves(
          transfers?.exists() ? ((transfers.data().rows ?? []) as TransferRow[]) : [],
        )
        if (table.exists()) {
          const d = table.data()
          setCap({
            rows: (d.rows ?? []) as CapRow[],
            total: Number(d.total ?? 0),
            builtAt: String(d.builtAt ?? ''),
          })
        }
      } catch (err) {
        if (live) setError(err instanceof Error ? err.message : 'Could not read your holding.')
      } finally {
        if (live) setLoading(false)
      }
    })()
    return () => {
      live = false
    }
  }, [linkedId])

  /**
   * The two things a receipt needs that are not on the holder's own record:
   * the company that received the money, and the round whose price they paid.
   *
   * A second pass rather than part of the load above, because the ids to ask
   * for are on the record the first pass fetches. Only the rounds this holder
   * actually has shares in are read — the rule allows `get` and not `list`
   * precisely so that is the only shape this can take.
   *
   * A failure here is deliberately silent. It costs the Receipt button and
   * nothing else, and an error banner over somebody's holding because a
   * settings document would not load is a worse screen than one without a
   * button.
   */
  const roundIds = useMemo(() => {
    const ids = [
      ...(me?.purchases ?? []).map((p) => p.issuanceId),
      ...(me?.options ?? []).map((o) => o.exerciseIssuanceId),
    ].filter(Boolean)
    return [...new Set(ids)].sort().join(',')
  }, [me])

  useEffect(() => {
    if (!me) return
    let live = true
    void (async () => {
      const ids = roundIds ? roundIds.split(',') : []
      const [co, rounds] = await Promise.all([
        getDoc(doc(db, 'skidaCrmSettings', 'company')).catch(() => null),
        Promise.all(
          ids.map((id) => getDoc(doc(db, 'skidaCrmIssuances', id)).catch(() => null)),
        ),
      ])
      if (!live) return
      if (co?.exists()) setCompany({ ...EMPTY_COMPANY, ...(co.data() as Partial<CompanyDetails>) })
      setIssuances(
        rounds
          .filter((d): d is NonNullable<typeof d> => Boolean(d?.exists()))
          .map((d) => ({ id: d.id, ...d.data() }) as Issuance),
      )
    })()
    return () => {
      live = false
    }
  }, [me, roundIds])

  if (loading) return <p className="empty-note">Loading…</p>

  if (!linkedId) {
    return (
      <p className="empty-note">
        Your sign-in is not yet linked to a shareholder record, so there is nothing to show. Ask
        Skida to connect it.
      </p>
    )
  }

  const myShares = cap?.rows.find((r) => r.name === me?.name)

  /**
   * Every payment they have made, by the key the history rows carry.
   *
   * One entry per receipt the company owes them. A row with no entry is a row
   * with nothing to give a receipt for — unpaid, or a transfer, where the
   * company was not the one receiving the money.
   */
  const paid = new Map(
    (me ? paidTransactions(me, issuances) : []).map((t) => [t.key, t] as const),
  )

  /**
   * Every acquisition they can be certified for, by the same key.
   *
   * A subset of `paid`: both documents need the money to have arrived, and the
   * certificate additionally needs the round reported to Foretaksregisteret. So
   * a row offers neither, the receipt alone, or both — never the certificate on
   * its own.
   */
  const certifiable = new Map(
    (me ? certifiableTransactions(me, issuances) : []).map((t) => [t.key, t] as const),
  )

  const openTx = openDoc
    ? (openDoc.kind === 'receipt' ? paid : certifiable).get(openDoc.key)
    : undefined

  /**
   * Everything that changed what they hold, oldest first.
   *
   * Transfers were a table of their own for a day and did not deserve one: a
   * holder with a single transfer got a whole container for one line, and the
   * question the section answers -- how did I come to own this -- is the same
   * question a purchase answers. So both are lines in one history, and shares
   * bought from a person read next to shares issued by the company.
   *
   * Sorted by date rather than left in stored order, which is the only thing
   * that makes a merged list readable. A movement with no date sorts last: it
   * is unfinished rather than ancient.
   */
  const history: Acquisition[] = [
    ...(me?.purchases ?? []).map((p, i): Acquisition => {
      // The same key `paidTransactions` builds, so a row can find its own
      // receipt — and its own price — without a second way of identifying a
      // payment.
      const key = `p-${p.issuanceId}-${i}`
      return {
        kind: 'purchase',
        key,
        date: p.date ?? '',
        shares: p.shares,
        /**
         * The price the receipt will state, not the one stored on the row.
         *
         * `pricePerShare` is absent whenever the holder paid the round's own
         * price, which is the ordinary case — so this line showed a price for
         * the handful who agreed their own and nothing at all for everybody
         * else. The rounds are loaded now, and `paidTransactions` has already
         * resolved it through `effectivePrice`. Unpaid rows keep the old
         * answer, because no round is fetched for a payment nobody made.
         */
        pricePerShare: paid.get(key)?.pricePerShare ?? p.pricePerShare ?? null,
        paidAt: p.paidAt ?? '',
      }
    }),
    // Exercised and paid only. An exercise awaiting the money is recorded and
    // counted nowhere, so putting it in a list of what they own would say the
    // shares exist a fortnight before they do.
    ...(me?.options ?? [])
      .map((o, i): Acquisition | null =>
        o.status === 'exercised' && o.paidDate
          ? {
              kind: 'exercise',
              key: `o-${o.grantDate}-${i}`,
              date: o.exerciseDate || o.grantDate,
              shares: o.count,
              pricePerShare: o.strikePrice,
              paidAt: o.paidDate,
            }
          : null,
      )
      .filter((o): o is Acquisition => o !== null),
    ...moves.map(
      (m, i): Acquisition => ({
        kind: 'transfer',
        key: `t-${m.date}-${i}`,
        date: m.date,
        shares: m.shares,
        pricePerShare: m.pricePerShare,
        direction: m.direction,
        counterparty: m.counterparty,
        settled: m.settled,
      }),
    ),
  ].sort((a, b) => (a.date ? 0 : 1) - (b.date ? 0 : 1) || a.date.localeCompare(b.date))

  return (
    <>
      <section className="outside__card">
        <h2 className="outside__title">Your holding</h2>
        {error && <p className="modal__error">{error}</p>}

        {myShares ? (
          <div className="outside__figures">
            <div>
              <dt>Shares</dt>
              <dd>{nf(myShares.shares)}</dd>
            </div>
            <div>
              <dt>Of the company</dt>
              <dd>{myShares.pct === null ? '—' : `${myShares.pct.toFixed(2).replace('.', ',')}%`}</dd>
            </div>
          </div>
        ) : (
          <p className="empty-note">
            No shares are registered against you today. A holding appears here once shares are
            issued or transferred to you.
          </p>
        )}

        {history.length > 0 && (
          <>
            <div className="card-label outside__sublabel">How you got them</div>
            <ul className="outside__list">
              {history.map((h) =>
                h.kind !== 'transfer' ? (
                  <li key={h.key}>
                    <strong>{nf(h.shares)}</strong> shares
                    {/* Where they came from, on the one line that can be two
                        things. Shares issued in a round and shares taken up on
                        a grant are both "the company issued these to you", and
                        only the second needs saying. */}
                    {h.kind === 'exercise' && <span className="muted"> from options</span>}
                    {h.date ? ` · ${formatDate(h.date)}` : ''}
                    {h.pricePerShare != null && (
                      <>
                        {` · ${fmtKrPrice(h.pricePerShare)}/share`}
                        <span className="muted">
                          {' '}
                          · {fmtKrSum(h.shares * h.pricePerShare)}
                        </span>
                      </>
                    )}
                    {/* Whether the money arrived, because it is their own answer
                        to give and they may be about to ask us for it. */}
                    <span className="muted">
                      {h.paidAt ? ` · paid ${formatDate(h.paidAt)}` : ' · not yet paid'}
                    </span>
                    {/*
                      One receipt per payment — 2 Sep 2026. Offered only where
                      the company actually received money, so an unpaid row has
                      no button rather than a button that produces a document
                      saying nothing was paid.
                    */}
                    {paid.has(h.key) && (
                      <button
                        className="outside__receipt"
                        onClick={() => setOpenDoc({ kind: 'receipt', key: h.key })}
                      >
                        Receipt
                      </button>
                    )}
                    {/*
                      And the aksjeeierbevis, once the round these shares came
                      from has been reported to Foretaksregisteret. Two separate
                      offers rather than one menu: they become available on
                      different days, and which one a holder can take is the
                      information.

                      Labelled in English like every other control here. The
                      document it opens is Norwegian by default and carries its
                      own switch; the button is interface.
                    */}
                    {certifiable.has(h.key) && (
                      <button
                        className="outside__receipt"
                        onClick={() => setOpenDoc({ kind: 'certificate', key: h.key })}
                      >
                        Share certificate
                      </button>
                    )}
                  </li>
                ) : (
                  <li key={h.key}>
                    {/* Signed, because a transfer is the one line here that can
                        take shares away as well as add them. */}
                    <strong className={h.direction === 'in' ? 'outside__in' : 'outside__out'}>
                      {h.direction === 'in' ? '+' : '−'}
                      {nf(h.shares)}
                    </strong>{' '}
                    shares
                    {h.date ? ` · ${formatDate(h.date)}` : ''}
                    {` · ${h.direction === 'in' ? 'from' : 'to'} ${h.counterparty}`}
                    {h.pricePerShare != null && ` · ${fmtKrPrice(h.pricePerShare)}/share`}
                    {/* An unsettled transfer is the one thing here somebody may
                        need to chase, so it says so rather than counting for
                        nothing in silence. */}
                    {h.settled ? (
                      <span className="muted"> · transferred</span>
                    ) : (
                      <span className="outside__pending"> pending board consent</span>
                    )}
                  </li>
                ),
              )}
            </ul>
          </>
        )}
      </section>

      <section className="outside__card">
        <h2 className="outside__title">The cap table</h2>
        <p className="outside__lede">
          Everybody who holds shares in Mountainsnow AS today, and how much of the company that is.
          {cap?.builtAt && (
            <>
              {' '}
              As at {formatDate(cap.builtAt.slice(0, 10))}.
            </>
          )}
        </p>

        {!cap ? (
          <p className="empty-note">The cap table has not been published yet.</p>
        ) : (
          <>
            <CapTablePie rows={cap.rows} total={cap.total} meName={me?.name} />

            <div className="outside__table">
              <div className="outside__row outside__row--head">
                <div>Shareholder</div>
                <div className="outside__num">Shares</div>
                <div className="outside__num">Owns</div>
              </div>
              {/* The rows in their own wrapper, so the heading above and the
                  total below stay put in the markup whatever the length. The
                  page is what scrolls — this box deliberately does not. */}
              <div className="outside__scroll">
                {cap.rows.map((r) => (
                  <div
                    className={`outside__row${r.name === me?.name ? ' outside__row--me' : ''}`}
                    key={r.name}
                  >
                    <div>{r.name}</div>
                    <div className="outside__num">{nf(r.shares)}</div>
                    <div className="outside__num">
                      {r.pct === null ? '—' : `${r.pct.toFixed(2).replace('.', ',')}%`}
                    </div>
                  </div>
                ))}
              </div>
              <div className="outside__row outside__row--total">
                <div>Total · {nf(cap.rows.length)} holders</div>
                <div className="outside__num">{nf(cap.total)}</div>
                <div className="outside__num">100,00%</div>
              </div>
            </div>
          </>
        )}
      </section>

      {/* Portalled to <body> by `PrintSheet`, so either document prints as the
          whole page rather than as an overlay inside the app. */}
      {openTx && me && openDoc?.kind === 'receipt' && (
        <ShareReceipt
          receipt={buildReceipt(
            me,
            openTx,
            company,
            issuances.find((i) => i.id === openTx.issuanceId),
          )}
          issuedOn={todayIso()}
          onClose={() => setOpenDoc(null)}
        />
      )}
      {/* The papers behind the numbers, after the numbers. The rest of what a
          shareholder gets — the feedback box, the perks, where to follow us —
          lives on the Home tab. */}
      <ShareholderArchiveCard docs={docs} />

      {openTx && me && openDoc?.kind === 'certificate' && (
        <ShareCertificate
          certificate={buildCertificate(
            me,
            openTx,
            company,
            issuances.find((i) => i.id === openTx.issuanceId),
            /*
              Only what Foretaksregisteret has been told about — 17 Sep 2026.
              This was the cap table's figure, which counts every round whether
              reported or not, so a certificate could state a holding larger than
              the public register knows of. Computed here from the holder's own
              record and their settled transfers, so it is true as at today and
              can say so.
            */
            me
              ? { shares: registeredShares(me, issuances, moves), asOf: todayIso() }
              : { shares: null, asOf: '' },
          )}
          issuedOn={todayIso()}
          onClose={() => setOpenDoc(null)}
        />
      )}
    </>
  )
}
